import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  ArrowLeft, Download, Loader2, Paperclip, FileText, Search, X, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, eur } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { printPdfBlobDirect } from "@/lib/directPrint";
import { openAttachmentViewer } from "@/lib/attachmentViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";

function DateField({ value, onChange, testid, label }) {
  const valore = value instanceof Date && !Number.isNaN(value.getTime())
    ? format(value, "yyyy-MM-dd")
    : "";

  return (
    <Input
      type="date"
      data-testid={testid}
      aria-label={label}
      title={`${label}: puoi digitare la data oppure usare il calendario`}
      value={valore}
      onChange={(event) => {
        const nuovoValore = event.target.value;
        onChange(
          nuovoValore
            ? new Date(`${nuovoValore}T00:00:00`)
            : null
        );
      }}
      className="h-9 text-sm"
    />
  );
}

export default function RegistroDDTPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [query, setQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [cantiereId, setCantiereId] = useState("Tutti");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [conAllegato, setConAllegato] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const params = useMemo(() => {
    const p = {};
    if (cantiereId !== "Tutti") p.cantiere_id = cantiereId;
    if (query.trim()) p.fornitore = query.trim();
    if (searchQuery.trim()) p.search = searchQuery.trim();
    if (dateFrom) p.date_from = format(dateFrom, "yyyy-MM-dd");
    if (dateTo) p.date_to = format(dateTo, "yyyy-MM-dd");
    if (conAllegato) p.con_allegato = true;
    return p;
  }, [cantiereId, query, searchQuery, dateFrom, dateTo, conAllegato]);

  useEffect(() => {
    api
      .get("/cantieri")
      .then((response) =>
        setCantieri(
          Array.isArray(response.data) ? response.data : []
        )
      )
      .catch(() => setCantieri([]));

    api
      .get("/gestionale/anagrafiche/fornitori")
      .then((response) => {
        const elenco = Array.isArray(response.data)
          ? response.data
          : [];

        const nomi = elenco
          .filter((item) => !item.archived_at)
          .map((item) => String(item.nome || "").trim())
          .filter(Boolean)
          .sort((a, b) =>
            a.localeCompare(b, "it", {
              sensitivity: "base",
            })
          );

        setFornitori(nomi);
      })
      .catch(() => setFornitori([]));
  }, []);
  useEffect(() => {
    setItems(null);
    api.get("/ddt", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [params]);

  const eliminaDdt = async (item) => {
    const numero = item.numero_ddt || "senza numero";
    if (!window.confirm(
      `Eliminare il DDT ${numero}? Verrà eliminato anche il materiale collegato dal cantiere.`
    )) return;

    try {
      await api.delete(`/materiali/${item.id}`);
      setItems((correnti) =>
        Array.isArray(correnti)
          ? correnti.filter((voce) => voce.id !== item.id)
          : []
      );
      toast.success("DDT e materiale collegato eliminati");
    } catch {
      toast.error("Impossibile eliminare il DDT");
    }
  };

  const total = (items || []).reduce((a, m) => a + (m.costo_totale || 0), 0);
  const bollaUrl = (p) => `${API}/uploads/bolla/${p}`;

  const exportPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/ddt/export/pdf", { params, responseType: "blob" });
      printPdfBlobDirect(new Blob([res.data], { type: "application/pdf" }));
    } catch (e) { toast.error("Errore export PDF"); }
    finally { setDownloading(false); }
  };

  const resetFilters = () => { setQuery(""); setSearchQuery(""); setCantiereId("Tutti"); setDateFrom(null); setDateTo(null); setConAllegato(false); };
  const hasFilters = query || searchQuery || cantiereId !== "Tutti" || dateFrom || dateTo || conAllegato;

  return (
    <AppShell>
      <button data-testid="back-button" onClick={() => navigate("/gestionale")}
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> Gestionale
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground flex items-center gap-2"><FileText className="h-4 w-4" /> Registro</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold mt-2">DDT & Bolle</h1>
          <p className="text-muted-foreground mt-2">Tutti i documenti di trasporto caricati, filtrabili ed esportabili.</p>
        </div>
        <Button onClick={exportPdf} disabled={downloading || !items || items.length === 0} data-testid="export-ddt-pdf-button"
          className="rounded-md font-bold h-11 px-6 transition-transform active:scale-[0.98]">
          {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Esporta PDF (lista + bolle)
        </Button>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-border bg-card p-3 sm:p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2.5">
          <div className="space-y-1.5 lg:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider">Ricerca</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Numero DDT, descrizione, fornitore, cantiere..." className="h-9 pl-9 text-sm" data-testid="ddt-text-search" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider">Fornitore</Label>
            <SearchableCombobox
              value={query}
              onValueChange={setQuery}
              options={[{ value: "", label: "Tutti i fornitori" }, ...fornitori.map((nome) => ({ value: nome, label: nome }))]}
              placeholder="Tutti i fornitori"
              searchPlaceholder="Cerca fornitore..."
              className="h-9 text-sm"
              testId="ddt-search-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider">Cantiere</Label>
            <SearchableCombobox value={cantiereId} onValueChange={setCantiereId}
              options={[{value:"Tutti",label:"Tutti i cantieri"},...cantieri.map(c=>({value:c.id,label:c.nome}))]}
              placeholder="Tutti i cantieri" searchPlaceholder="Scrivi il cantiere..." className="h-9 text-sm" testId="ddt-cantiere-filter" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider">Dal</Label>
            <DateField value={dateFrom} onChange={setDateFrom} testid="ddt-date-from" label="Data inizio" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider">Al</Label>
            <DateField value={dateTo} onChange={setDateTo} testid="ddt-date-to" label="Data fine" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-3">
          <button data-testid="ddt-con-allegato-toggle" onClick={() => setConAllegato((v) => !v)}
            className={`inline-flex items-center gap-2 text-sm font-medium rounded-md px-3 py-1.5 border transition-colors ${conAllegato ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <Paperclip className="h-4 w-4" /> Solo con bolla allegata
          </button>
          {hasFilters && (
            <Button variant="ghost" size="sm" data-testid="ddt-reset-filters" onClick={resetFilters} className="text-muted-foreground">
              <X className="h-4 w-4 mr-1" /> Azzera filtri
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-6 mb-4 px-1">
        <p className="text-sm text-muted-foreground"><span className="font-heading text-2xl font-bold text-foreground">{items ? items.length : "—"}</span> voci</p>
        <p className="text-sm text-muted-foreground">Totale <span className="font-heading text-2xl font-bold text-primary">{eur(total)}</span></p>
      </div>

      {/* Table */}
      {items === null ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-16 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground/40" />
          <p className="font-heading text-xl font-semibold mt-4">Nessun DDT</p>
          <p className="text-muted-foreground mt-1">Nessun documento corrisponde ai filtri selezionati.</p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Data</TableHead>
                <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Cantiere</TableHead>
                <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">N. DDT</TableHead>
                <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Fornitore</TableHead>
                <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Lavorazione</TableHead>
                <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Bolla</TableHead>
                <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">Totale</TableHead>
                <TableHead className="w-14 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((m) => (
                <TableRow key={m.id} data-testid={`ddt-row-${m.id}`} className="hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/cantiere/${m.cantiere_id}`)}>
                  <TableCell>{format(new Date(m.data_acquisto), "dd/MM/yyyy")}</TableCell>
                  <TableCell className="font-semibold">{m.cantiere_nome}</TableCell>
                  <TableCell>{m.numero_ddt || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-muted-foreground">{m.fornitore || "—"}</TableCell>
                  <TableCell>{m.lavorazione_nome ? <Badge variant="outline" className="border-secondary/30 text-secondary bg-secondary/5">{m.lavorazione_nome}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {m.allegato_path ? (
                      <button type="button" onClick={() => openAttachmentViewer(bollaUrl(m.allegato_path), m.allegato_nome || `DDT ${m.numero_ddt || ""}`)} data-testid={`ddt-bolla-view-${m.id}`} className="text-primary hover:underline inline-flex items-center gap-1 text-sm font-medium"><Paperclip className="h-3.5 w-3.5" /> Vedi</button>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-semibold">{eur(m.costo_totale)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      title="Elimina DDT e materiale collegato"
                      onClick={() => eliminaDdt(m)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      )}
    </AppShell>
  );
}
